// Onboarding-Wizard Tier A/B + Autorisierungserfassung (#160).
//
// Geführter, mehrstufiger Ablauf, der die bestehenden Onboarding-Bausteine zu einem Vertrag
// zusammenfasst: Tier wählen → Discovery → Vorschlag → Bestätigung → schriftliche Freigabe. Die
// Freigabe wird erfasst und beim Bestätigen ans Backend gereicht, das sie revisionssicher in den
// Audit-Trail schreibt (kein neuer Audit-Mechanismus — die vorhandene HMAC-Hash-Kette).
//
// Zwei Tiers, ein Fluss:
//   - Tier-A (OPC UA): read-only Browse ab dem Objects-Ordner schlägt Tag-Kandidaten mit OPC-Bindung
//     vor (Draft #152). Kein blinder Scan, es wird nichts an die Anlage geschrieben.
//   - Tier-B (Modbus): eine Register-Map (CSV/Hersteller-Tabelle) wird importiert und je Zeile ein
//     Register→Tag-Vorschlag abgeleitet (Import #156). Kein Adressraum-Sweep, kein Paket an die Anlage.
//
// Prinzip über beide Tiers: Discovery ist gefragt und passiv/read-only; jeder Kandidat ist
// niedrig-konfident und human-bestätigt; erst mit erfasster schriftlicher Freigabe wird der Katalog
// geschrieben. Control bleibt lokal.

import { useCallback, useMemo, useRef, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import {
  confirmModbusMap,
  importModbusMap,
  uaBrowseConfirm,
  uaBrowseDraft,
  ApiError,
} from '../api/client';
import type {
  ModbusMapDecision,
  ModbusMapProposal,
  OnboardingAuthorization,
  UaBrowseDecision,
  UaBrowseDraftResult,
} from '../api/types';
import { Button, LabeledInput, PanelHeading } from '../components/ui';

export type OnboardingTier = 'A' | 'B';
type WizardStep = 'tier' | 'discovery' | 'review' | 'authorize' | 'done';

export interface OnboardingWizardDialogProps {
  projectId: string;
  onClose: () => void;
  // Nach erfolgreichem Confirm: der Aufrufer lädt den Katalog neu (rev + Tier als Info).
  onConfirmed: (rev: string, tier: OnboardingTier) => void | Promise<void>;
}

// Ein tier-neutraler Kandidat für die Review-Tabelle. ``key`` ist der stabile Schlüssel (Tag für
// Tier-A, Zeilennummer für Tier-B), damit dieselbe Tabelle beide Tiers rendern kann.
interface Candidate {
  key: string;
  tag: string;
  detail: string; // NodeId/Register + Datentyp (tier-spezifisch, nur Anzeige)
  confidence: number;
  bindable: boolean; // Tier-B: nicht-bindbare Zeilen können nur verworfen werden
}

const STEP_ORDER: WizardStep[] = ['tier', 'discovery', 'review', 'authorize', 'done'];

function nowLocalIso(): string {
  // ISO-8601 ohne Millisekunden, für das datetime-local-Feld (YYYY-MM-DDTHH:mm).
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function OnboardingWizardDialog({
  projectId,
  onClose,
  onConfirmed,
}: OnboardingWizardDialogProps): JSX.Element {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<WizardStep>('tier');
  const [tier, setTier] = useState<OnboardingTier | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tier-A-Zustand.
  const [endpoint, setEndpoint] = useState('opc.tcp://');
  const [uaDraft, setUaDraft] = useState<UaBrowseDraftResult | null>(null);

  // Tier-B-Zustand.
  const [proposals, setProposals] = useState<ModbusMapProposal[]>([]);

  // Gemeinsam: Entscheidung je Kandidat (true = bestätigt/übernehmen, false = verworfen).
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});

  // Freigabe-Erfassung.
  const [grantedBy, setGrantedBy] = useState('');
  const [grantedAt, setGrantedAt] = useState(nowLocalIso());
  const [scope, setScope] = useState('');
  const [savedRev, setSavedRev] = useState<string | null>(null);

  const reset = useCallback(() => {
    setError(null);
    setUaDraft(null);
    setProposals([]);
    setConfirmed({});
    setSavedRev(null);
  }, []);

  const chooseTier = useCallback(
    (chosen: OnboardingTier) => {
      reset();
      setTier(chosen);
      setStep('discovery');
    },
    [reset],
  );

  // Tier-A: read-only Browse.
  const runBrowse = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await uaBrowseDraft(projectId, endpoint.trim());
      setUaDraft(res);
      const init: Record<string, boolean> = {};
      for (const v of res.catalog.variables) init[v.tag] = true;
      setConfirmed(init);
      setStep('review');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [projectId, endpoint]);

  // Tier-B: Register-Map importieren.
  const runImport = useCallback(
    async (file: File) => {
      setBusy(true);
      setError(null);
      try {
        const res = await importModbusMap(projectId, file);
        setProposals(res.proposals);
        // Startzustand: bindbare Zeilen übernehmen, nicht bindbare verwerfen. Bestätigung bleibt Pflicht.
        const init: Record<string, boolean> = {};
        for (const p of res.proposals) init[String(p.line)] = p.binding !== null;
        setConfirmed(init);
        setStep('review');
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
        );
      } finally {
        setBusy(false);
      }
    },
    [projectId],
  );

  // Tier-neutrale Kandidatenliste für die Review-Tabelle.
  const candidates = useMemo<Candidate[]>(() => {
    if (tier === 'A' && uaDraft) {
      return uaDraft.catalog.variables.map((v) => {
        const row = uaDraft.review.tags.find((r) => r.tag === v.tag);
        return {
          key: v.tag,
          tag: v.tag,
          detail: `${v.opc?.node_id ?? ''} · ${v.opc?.datatype ?? ''} · ${v.unit}`,
          confidence: row?.confidence ?? 0.5,
          bindable: true,
        };
      });
    }
    if (tier === 'B') {
      return proposals.map((p) => ({
        key: String(p.line),
        tag: p.tag,
        detail:
          p.binding !== null
            ? `${p.binding.kind} ${p.binding.address} · ${p.binding.datatype}`
            : t('onboardingWizard.notBindable'),
        confidence: p.confidence,
        bindable: p.binding !== null,
      }));
    }
    return [];
  }, [tier, uaDraft, proposals, t]);

  const toggle = useCallback((key: string) => {
    setConfirmed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const confirmedCount = useMemo(
    () => candidates.filter((c) => confirmed[c.key]).length,
    [candidates, confirmed],
  );

  // Bestätigung + erfasste Freigabe → schreiben. Die Freigabe wird IMMER mitgeschickt (dieser
  // Schritt ist die schriftliche Freigabe des Wizards), sodass sie im Audit-Trail landet.
  const runConfirm = useCallback(async () => {
    if (!tier) return;
    setBusy(true);
    setError(null);
    try {
      const authorization: OnboardingAuthorization = {
        granted_by: grantedBy.trim(),
        granted_at: new Date(grantedAt).toISOString(),
        scope: scope.trim(),
      };
      let rev: string;
      if (tier === 'A' && uaDraft) {
        const decisions: UaBrowseDecision[] = uaDraft.catalog.variables.map((v) => ({
          tag: v.tag,
          confirmed: confirmed[v.tag] ?? false,
        }));
        const res = await uaBrowseConfirm(projectId, uaDraft.catalog, decisions, authorization);
        rev = res.rev;
      } else {
        const decisions: ModbusMapDecision[] = proposals.map((p) => ({
          line: p.line,
          confirmed: confirmed[String(p.line)] ?? false,
        }));
        const res = await confirmModbusMap(projectId, proposals, decisions, authorization);
        rev = res.rev;
      }
      setSavedRev(rev);
      setStep('done');
      await onConfirmed(rev, tier);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      );
    } finally {
      setBusy(false);
    }
  }, [tier, uaDraft, proposals, confirmed, grantedBy, grantedAt, scope, projectId, onConfirmed]);

  const confidencePct = (c: number): string => `${Math.round(Math.max(0, Math.min(1, c)) * 100)}%`;
  const canBrowse = endpoint.trim().length >= 'opc.tcp://x'.length;
  const authComplete = grantedBy.trim().length > 0 && grantedAt.trim().length > 0;
  const stepIndex = STEP_ORDER.indexOf(step);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('onboardingWizard.title')}
      data-testid="onboarding-wizard"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-4 overflow-y-auto rounded-lg border border-bf-border bg-bf-navy p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-bf-white">{t('onboardingWizard.title')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            data-testid="onboarding-wizard-close"
            onClick={onClose}
            className="text-bf-muted hover:text-bf-white"
          >
            ✕
          </button>
        </div>

        {/* Schritt-Anzeige: Discovery → Vorschlag → Bestätigung → Freigabe. */}
        <ol
          className="flex flex-wrap gap-2 text-[11px] text-bf-muted"
          data-testid="onboarding-wizard-steps"
        >
          {(['tier', 'discovery', 'review', 'authorize'] as WizardStep[]).map((s, i) => (
            <li
              key={s}
              className={`rounded px-2 py-0.5 ${
                STEP_ORDER.indexOf(s) <= stepIndex
                  ? 'bg-bf-gold/20 text-bf-gold'
                  : 'bg-bf-border/40'
              }`}
              data-testid={`onboarding-wizard-step-label-${s}`}
            >
              {i + 1}. {t(`onboardingWizard.stepName.${s}`)}
            </li>
          ))}
        </ol>

        {/* Schritt 1: Tier wählen. */}
        {step === 'tier' && (
          <div className="flex flex-col gap-3" data-testid="onboarding-wizard-tier">
            <p className="text-xs text-bf-muted">{t('onboardingWizard.tierIntro')}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                data-testid="onboarding-wizard-tier-A"
                onClick={() => chooseTier('A')}
                className="flex flex-col gap-1 rounded border border-bf-border p-3 text-left hover:border-bf-gold"
              >
                <span className="text-sm font-medium text-bf-white">
                  {t('onboardingWizard.tierA')}
                </span>
                <span className="text-[11px] text-bf-muted">{t('onboardingWizard.tierADesc')}</span>
              </button>
              <button
                type="button"
                data-testid="onboarding-wizard-tier-B"
                onClick={() => chooseTier('B')}
                className="flex flex-col gap-1 rounded border border-bf-border p-3 text-left hover:border-bf-gold"
              >
                <span className="text-sm font-medium text-bf-white">
                  {t('onboardingWizard.tierB')}
                </span>
                <span className="text-[11px] text-bf-muted">{t('onboardingWizard.tierBDesc')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Schritt 2: Discovery (tier-spezifisch, passiv/read-only). */}
        {step === 'discovery' && tier === 'A' && (
          <div className="flex flex-col gap-3" data-testid="onboarding-wizard-discovery-A">
            <LabeledInput
              label={t('onboardingWizard.endpoint')}
              value={endpoint}
              onChange={setEndpoint}
              placeholder="opc.tcp://plc:4840"
            />
            <div
              className="rounded border border-bf-gold/40 bg-bf-gold/5 p-2 text-xs text-bf-gold"
              data-testid="onboarding-wizard-readonly-note"
            >
              {t('onboardingWizard.readOnlyNoteA')}
            </div>
            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={busy || !canBrowse}
                onClick={() => void runBrowse()}
                testId="onboarding-wizard-browse"
              >
                {busy ? t('onboardingWizard.discovering') : t('onboardingWizard.browse')}
              </Button>
            </div>
          </div>
        )}

        {step === 'discovery' && tier === 'B' && (
          <div className="flex flex-col gap-3" data-testid="onboarding-wizard-discovery-B">
            <div
              className="rounded border border-bf-gold/40 bg-bf-gold/5 p-2 text-xs text-bf-gold"
              data-testid="onboarding-wizard-readonly-note"
            >
              {t('onboardingWizard.readOnlyNoteB')}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.txt,text/csv"
              className="hidden"
              data-testid="onboarding-wizard-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void runImport(file);
              }}
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                testId="onboarding-wizard-import"
              >
                {busy ? t('onboardingWizard.importing') : t('onboardingWizard.chooseFile')}
              </Button>
            </div>
          </div>
        )}

        {/* Schritt 3: Vorschlag + Bestätigung (tier-neutrale Kandidatenliste). */}
        {step === 'review' && (
          <div className="flex flex-col gap-3" data-testid="onboarding-wizard-review">
            <div className="flex items-center justify-between">
              <PanelHeading>{t('onboardingWizard.candidates')}</PanelHeading>
              <span className="text-xs text-bf-muted" data-testid="onboarding-wizard-count">
                {t('onboardingWizard.confirmedCount', {
                  confirmed: confirmedCount,
                  total: candidates.length,
                })}
              </span>
            </div>

            {candidates.length === 0 && (
              <p className="text-xs text-amber-300" data-testid="onboarding-wizard-empty">
                {t('onboardingWizard.empty')}
              </p>
            )}

            <ul className="flex flex-col gap-1" data-testid="onboarding-wizard-list">
              {candidates.map((c) => {
                const isOn = confirmed[c.key] ?? false;
                return (
                  <li
                    key={c.key}
                    className={`flex items-center gap-3 rounded border p-2 ${
                      isOn ? 'border-bf-gold/50 bg-bf-navy-light/40' : 'border-bf-border opacity-60'
                    }`}
                    data-testid={`onboarding-wizard-row-${c.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={isOn}
                      disabled={!c.bindable}
                      onChange={() => toggle(c.key)}
                      aria-label={t('onboardingWizard.confirmTag', { tag: c.tag })}
                      data-testid={`onboarding-wizard-check-${c.key}`}
                      className="h-4 w-4 accent-bf-gold"
                    />
                    <div className="min-w-0 flex-1">
                      <span className="truncate text-sm font-medium text-bf-white" title={c.tag}>
                        {c.tag}
                      </span>
                      <div className="truncate text-[11px] text-bf-muted" title={c.detail}>
                        {c.detail}
                      </div>
                    </div>
                    <span
                      className="shrink-0 text-[11px] text-bf-muted"
                      title={t('onboardingWizard.confidence')}
                      data-testid={`onboarding-wizard-conf-${c.key}`}
                    >
                      {confidencePct(c.confidence)}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={busy || candidates.length === 0}
                onClick={() => setStep('authorize')}
                testId="onboarding-wizard-to-authorize"
              >
                {t('onboardingWizard.toAuthorize', { confirmed: confirmedCount })}
              </Button>
            </div>
          </div>
        )}

        {/* Schritt 4: schriftliche Freigabe erfassen. */}
        {step === 'authorize' && (
          <div className="flex flex-col gap-3" data-testid="onboarding-wizard-authorize">
            <p className="text-xs text-bf-muted">{t('onboardingWizard.authorizeIntro')}</p>
            <div
              className="rounded border border-bf-border bg-bf-navy-light/40 p-2 text-[11px] text-bf-muted"
              data-testid="onboarding-wizard-authorize-summary"
            >
              {t('onboardingWizard.authorizeSummary', {
                tier: tier === 'A' ? t('onboardingWizard.tierA') : t('onboardingWizard.tierB'),
                confirmed: confirmedCount,
              })}
            </div>
            <LabeledInput
              label={t('onboardingWizard.grantedBy')}
              value={grantedBy}
              onChange={setGrantedBy}
              placeholder={t('onboardingWizard.grantedByPlaceholder')}
            />
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">
                {t('onboardingWizard.grantedAt')}
              </span>
              <input
                type="datetime-local"
                value={grantedAt}
                data-testid="onboarding-wizard-granted-at"
                onChange={(e) => setGrantedAt(e.target.value)}
                className="w-full rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
              />
            </label>
            <LabeledInput
              label={t('onboardingWizard.scope')}
              value={scope}
              onChange={setScope}
              placeholder={t('onboardingWizard.scopePlaceholder')}
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setStep('review')} testId="onboarding-wizard-back">
                {t('onboardingWizard.back')}
              </Button>
              <Button
                variant="primary"
                disabled={busy || !authComplete}
                onClick={() => void runConfirm()}
                testId="onboarding-wizard-confirm"
              >
                {busy ? t('onboardingWizard.saving') : t('onboardingWizard.confirmAndWrite')}
              </Button>
            </div>
          </div>
        )}

        {/* Schritt 5: Abschluss. */}
        {step === 'done' && savedRev && (
          <div
            className="rounded border border-green-500/50 bg-green-950/20 p-3 text-sm text-green-300"
            data-testid="onboarding-wizard-done"
          >
            <p>{t('onboardingWizard.saved', { rev: savedRev })}</p>
            <p className="mt-1 text-[11px] text-green-300/80">{t('onboardingWizard.auditNote')}</p>
          </div>
        )}

        {error && (
          <p className="text-xs text-red-300" data-testid="onboarding-wizard-error">
            {error}
          </p>
        )}

        <div className="flex justify-end">
          <Button onClick={onClose} testId="onboarding-wizard-cancel">
            {step === 'done' ? t('common.close') : t('onboardingWizard.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
